/**
 * AdContainer - Cross-platform ad display component
 *
 * Web: Shows AdSense ad unit or placeholder (during development)
 * Native: AdMob ads are shown as fullscreen overlays via adService
 */

import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import adService, { AdState, ADSENSE_CONFIG } from "src/services/adService";

interface AdContainerProps {
  // Callback when ad completes (user can proceed)
  onAdComplete?: () => void;
  // Callback when ad fails
  onAdError?: (error: string) => void;
  // Callback when ad is skipped
  onAdSkipped?: () => void;
  // Duration to show the ad/placeholder (in ms) - for web simulation
  displayDuration?: number;
  // Whether the ad container is visible/active
  isActive?: boolean;
}

const AdContainer: React.FC<AdContainerProps> = ({ onAdComplete, onAdError, onAdSkipped, displayDuration = 5000, isActive = true }) => {
  const [adState, setAdState] = useState<AdState>("idle");
  const [countdown, setCountdown] = useState<number>(Math.ceil(displayDuration / 1000));
  const [adLoaded, setAdLoaded] = useState(false);
  const adContainerRef = useRef<HTMLDivElement>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup function
  const cleanup = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => cleanup();
  }, []);

  useEffect(() => {
    if (!isActive) {
      cleanup();
      setAdState("idle");
      setCountdown(Math.ceil(displayDuration / 1000));
      return;
    }

    if (adService.isWebPlatform()) {
      // Web: Load AdSense or show placeholder
      loadWebAd();
    } else {
      // Native: AdMob ads are handled differently (fullscreen overlay)
      // The native ad is triggered from FreeModelDrawer
      setAdState("ready");
    }
  }, [isActive]);

  const loadWebAd = async () => {
    setAdState("loading");

    try {
      // Try to load AdSense
      if (ADSENSE_CONFIG.testMode) {
        // In test mode, simulate ad loading
        await new Promise((resolve) => setTimeout(resolve, 500));
        setAdState("showing");
        setAdLoaded(true);
        startCountdown();
      } else {
        // Production: load real AdSense
        const loaded = await adService.loadAdSenseScript();
        if (loaded) {
          setAdState("showing");
          setAdLoaded(true);
          // Trigger AdSense ad push
          adService.refreshAdSenseAd();
          startCountdown();
        } else {
          throw new Error("Failed to load AdSense");
        }
      }
    } catch (error) {
      console.error("[AdContainer] Failed to load ad:", error);
      setAdState("failed");
      onAdError?.((error as Error)?.message || "Failed to load ad");
    }
  };

  // Start the countdown timer
  const startCountdown = () => {
    const totalSeconds = Math.ceil(displayDuration / 1000);
    setCountdown(totalSeconds);

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          cleanup();
          setAdState("completed");
          onAdComplete?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Render different states
  const renderContent = () => {
    switch (adState) {
      case "idle":
      case "loading":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={40} sx={{ color: PureLightThemeCaaS.colors.alpha.white[70] }} />
            <Typography
              variant="body2"
              sx={{
                color: PureLightThemeCaaS.colors.alpha.white[70],
              }}
            >
              Loading ad...
            </Typography>
          </Box>
        );

      case "showing":
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {/* Countdown timer */}
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 2,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                borderRadius: "4px",
                px: 0.5,
                py: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: PureLightThemeCaaS.colors.alpha.white[100],
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                {countdown}s
              </Typography>
            </Box>

            {/* Ad content */}
            {ADSENSE_CONFIG.testMode ? (
              // Test mode: show placeholder
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#1a1a1a",
                  borderRadius: "8px",
                  border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[20]}`,
                }}
              >
                {/* Simulated video ad placeholder */}
                <Box
                  sx={{
                    width: "80%",
                    height: "70%",
                    backgroundColor: "#2a2a2a",
                    borderRadius: "4px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {/* Animated gradient to simulate video */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(45deg, #333 25%, #444 50%, #333 75%)",
                      backgroundSize: "200% 200%",
                      animation: "shimmer 2s ease-in-out infinite",
                      "@keyframes shimmer": {
                        "0%": { backgroundPosition: "200% 200%" },
                        "100%": { backgroundPosition: "0% 0%" },
                      },
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: PureLightThemeCaaS.colors.alpha.white[70],
                      position: "relative",
                      zIndex: 1,
                      textAlign: "center",
                      px: 2,
                    }}
                  >
                    🎬 Ad Preview
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: PureLightThemeCaaS.colors.alpha.white[50],
                      position: "relative",
                      zIndex: 1,
                      mt: 1,
                      textAlign: "center",
                      px: 2,
                    }}
                  >
                    (Test Mode - No real ad)
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: PureLightThemeCaaS.colors.alpha.orange[100],
                    fontWeight: "bold",
                  }}
                >
                  Watch to use Free Model
                </Typography>
              </Box>
            ) : (
              // Production: AdSense ad slot
              <Box
                ref={adContainerRef}
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* AdSense ad unit - uncomment when you have real AdSense credentials */}
                {/* 
                <ins
                  className="adsbygoogle"
                  style={{ display: "block", width: "100%", height: "100%" }}
                  data-ad-client={ADSENSE_CONFIG.publisherId}
                  data-ad-slot={ADSENSE_CONFIG.adSlotId}
                  data-ad-format="auto"
                  data-full-width-responsive="true"
                />
                */}
                <Typography
                  variant="body2"
                  sx={{
                    color: PureLightThemeCaaS.colors.alpha.white[50],
                  }}
                >
                  Ad loading...
                </Typography>
              </Box>
            )}
          </Box>
        );

      case "completed":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: PureLightThemeCaaS.colors.alpha.white[100],
              }}
            >
              ✓ Ad Complete
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: PureLightThemeCaaS.colors.alpha.white[70],
              }}
            >
              Free Model unlocked!
            </Typography>
          </Box>
        );

      case "failed":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: PureLightThemeCaaS.colors.alpha.white[70],
              }}
            >
              ⚠️ Ad unavailable
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: PureLightThemeCaaS.colors.alpha.white[50],
                textAlign: "center",
              }}
            >
              Please try again later
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "800px",
        aspectRatio: "16/9",
        backgroundColor: PureLightThemeCaaS.colors.alpha.white[10],
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
        overflow: "hidden",
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default AdContainer;
